import { useState, useCallback, useMemo } from 'react';
import { PostConsignment, handleDeleteConsignment } from '../../../api/GBENSDec';
import { getEORIValidation } from '../../../api/util';

const useConsignment = (
  currentConsignment,
  setCurrentConsignment,
  currentConsignmentErrors,
  setCurrentConsignmentErrors,
  currentConsignmentHMRCErrors,
  setCurrentConsignmentHMRCErrors,
  data,
  setData,
  errors,
  setErrors,
  errorsHMRC,
  setErrorsHMRC,
  headerId,
  refreshNeenors
) => {
  const [currentLine, setCurrentLine] = useState(null);

  const getNewLineID = useCallback(() => {
    if (!currentConsignment || !Array.isArray(currentConsignment.lines)) {
      console.error('currentConsignment or lines is null/undefined:', currentConsignment);
      return -100000;
    }
    return currentConsignment.lines.reduce((p, c) => {
      if (c.id >= 0) return p;
      return Math.max(p, c.id + 1);
    }, -100000);
  }, [currentConsignment]);

  const hasConsignments = useMemo(() => data.Consignments?.length > 0, [data]);

  const addNewConsignment = useCallback(() => {
    const c = { lines: [], Consignee: {}, Consignor: {} };
    setCurrentConsignment(c);
    const e = { lines: [] };
    setCurrentConsignmentErrors(e);
    const e2 = { lines: [] };
    setCurrentConsignmentHMRCErrors(e2);
  }, [data, errors]);

  const editConsignment = useCallback(
    (id) => {
      const c = data.Consignments.find((c) => c.id === id);
      if (!c.Consignor) c.Consignor = {};
      if (!c.Consignee) c.Consignee = {};
      setCurrentConsignment(c);
      const e = errors.Consignments?.find?.((e) => e.id === id);
      setCurrentConsignmentErrors(e);
      const e2 = errorsHMRC.Consignments?.find?.((e) => e.id === id);
      setCurrentConsignmentHMRCErrors(e2);
    },
    [data, errors]
  );

  const updateCurrentConsignment = useCallback(
    (e, field, modal) => {
      const value = e.target.value;
      if (!modal)
        setCurrentConsignment((prev) => ({
          ...prev,
          [field]: value,
        }));
      else if (currentConsignment[modal])
        setCurrentConsignment((prev) => ({
          ...prev,
          [modal]: {
            ...prev[modal],
            [field]: value,
          },
        }));
      else
        setCurrentConsignment((prev) => ({
          ...prev,
          [modal]: {
            [field]: value,
          },
        }));
    },
    [currentConsignment]
  );

  const saveCurrentConsignment = async () => {
    // we don't want to mutate the currentConsignment object
    const consignmentToSave = structuredClone(currentConsignment);

    if (currentConsignment?.Consignee?.EORI) {
      let hasValidGBEORI = 0;

      // determine if the Consignee address details should be sent to HRMC (ticket ENS-683)
      if (currentConsignment.Consignee.EORI.startsWith('GB')) {
        const validateEori = await getEORIValidation(currentConsignment.Consignee.EORI, 'true');
        hasValidGBEORI = validateEori.statusDescr === 'In Valid' ? 0 : 1;
      }

      consignmentToSave.Consignee.valid = hasValidGBEORI;
    }

    // loop through currentConsignment lines and make sure dangerousGoods is 4 characters or less
    // also remove any non-numeric characters just in case, and ensure it's a string
    consignmentToSave.lines = consignmentToSave.lines.map((line) => {
      return {
        ...line,
        dangerousGoods: line.dangerousGoods?.toString().replace(/\D/g, '').slice(-4) || '',
      };
    });

    const dt = await PostConsignment({
      headerId,
      Consignments: [consignmentToSave],
    });
    await refreshNeenors();

    let id;
    let itemIds = [];
    if (dt?.[0]?.consignmentId) {
      id = dt[0].consignmentId;
      if (dt[0].insertedConsignmentItemIds) {
        itemIds = JSON.parse(dt[0].insertedConsignmentItemIds);
      }
    } else if (dt?.[0]?.ErrorMessage) {
      return dt;
    } else {
      throw new Error('Error saving consignment');
    }
    const idMap = currentConsignment.lines.map((l, i) => ({ prev: l.id, new: itemIds[i].id }));
    setData((prev) => ({
      ...prev,
      Consignments: [
        ...prev.Consignments.filter((c) => c.id !== currentConsignment.id),
        {
          ...currentConsignment,
          lines: currentConsignment.lines.map((l, i) => {
            return { ...l, id: itemIds[i].id };
          }),
          id,
        },
      ],
    }));
    setErrors((prev) => {
      const filtered = prev.Consignments ? prev.Consignments.filter((c) => c.id !== currentConsignmentErrors?.id) : [];
      return {
        ...prev,
        Consignments: [
          ...filtered,
          {
            ...currentConsignmentErrors,
            lines: currentConsignmentErrors.lines.map((l, i) => {
              return { ...l, id: idMap.find((i) => i.prev === l.id).new };
            }),
            id,
          },
        ],
      };
    });
    setErrorsHMRC((prev) => {
      const filtered = prev.Consignments
        ? prev.Consignments.filter((c) => c.id !== currentConsignmentHMRCErrors.id)
        : [];
      return {
        ...prev,
        Consignments: [...filtered, { ...currentConsignmentHMRCErrors, id }],
      };
    });
  };

  const moveConsignmentData = useCallback(
    (modalFrom, modalTo, deleteModalFrom) => {
      if (!deleteModalFrom)
        setCurrentConsignment((prev) => ({
          ...prev,
          [modalTo]: prev?.[modalFrom] ? JSON.parse(JSON.stringify(prev[modalFrom])) : {},
        }));
      else
        setCurrentConsignment((prev) => ({
          ...prev,
          [modalTo]: prev?.[modalFrom] ? JSON.parse(JSON.stringify(prev[modalFrom])) : {},
          [modalFrom]: undefined,
        }));
    },
    [currentConsignment]
  );

  const setBlankConsignmentData = useCallback(
    (modal, newData) => {
      setCurrentConsignment((prev) => ({
        ...prev,
        [modal]: { ...newData, id: -1 },
      }));
    },
    [currentConsignment]
  );

  const selectNeenor = (modal, value) => {
    setCurrentConsignment((prev) => ({
      ...prev,
      [modal]: value,
    }));
  };

  const deleteConsignmentData = useCallback(
    (modal) => {
      setCurrentConsignment((prev) => ({
        ...prev,
        [modal]: undefined,
      }));
    },
    [currentConsignment]
  );

  const addNewConsignmentLine = useCallback(() => {
    const l = { id: getNewLineID(), packagingMarks: 'As Addressed' };
    setCurrentLine(l);
  }, [currentLine, currentConsignment]);

  const updateCurrentLine = useCallback(
    (e, field) => {
      const value = e.target.value;
      setCurrentLine((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [currentLine]
  );

  const editConsignmentLine = useCallback(
    (id) => {
      setCurrentLine({ ...currentConsignment.lines.find((l) => l.id === id) });
    },
    [currentConsignment]
  );

  const saveConsignmentLine = useCallback(() => {
    setCurrentConsignment((prev) => {
      if (!prev) return { lines: [currentLine] };
      if (!Array.isArray(prev.lines)) return { ...prev, lines: [currentLine] };

      const newLines = [...(prev.lines || []).filter((p) => p.id !== currentLine.id), currentLine].sort((a, b) => {
        if (a.id < 0 && b.id > 0) return 1;
        else if (a.id > 0 && b.id < 0) return -1;
        else return a.id - b.id;
      });

      return {
        ...prev,
        lines: newLines,
      };
    });
  }, [currentLine]);

  const deleteConsignmentLine = useCallback(() => {
    setCurrentConsignment((prev) => ({
      ...prev,
      lines: [...prev.lines.filter((l) => l.id !== currentLine.id)],
    }));
    setCurrentLine(null);
  }, [currentLine]);

  const deleteConsignment = useCallback(
    async (ids) => {
      // Wrap a single id in an array
      const idsArray = Array.isArray(ids) ? ids : [ids];
      // Convert the selected IDs to numbers (in case they come as strings)
      const numericIds = idsArray.map((id) => Number(id));

      // Call the API with the array of numeric IDs
      const result = await handleDeleteConsignment(numericIds);
      if (result?.message !== 'Consignment deleted') {
        return;
      }

      // Update the data state by filtering out all consignments whose id is in numericIds
      setData((prev) => ({
        ...prev,
        Consignments: prev.Consignments?.filter((consignment) => !numericIds.includes(consignment.id)),
      }));

      // Similarly update errors (if errors.Consignments exists as an array)
      setErrors((prev) => ({
        ...prev,
        Consignments: prev.Consignments?.filter((consignment) => !numericIds.includes(consignment.id)),
      }));

      setErrorsHMRC((prev) => ({
        ...prev,
        Consignments: prev.Consignments?.filter((consignment) => !numericIds.includes(consignment.id)),
      }));

      setCurrentConsignment(null);
    },
    [data, errors]
  );

  const handleGetMRNDetails = (data) => {
    //if data is an empty array, set current consignment to null
    if (data.length === 0) {
      setCurrentConsignment({ lines: [], Consignee: {}, Consignor: {} });
    } else {
      let idCount = -100000;
      const lines = [];
      //consignmentReference
      updateCurrentConsignment({ target: { value: data[0].consignmentReference } }, 'consignmentReference');
      const consignorData = {
        companyName: data[0]?.Consignor?.companyName || '',
        EORI: data[0]?.Consignor?.EORI || '',
        streetAddress: data[0]?.Consignor?.streetAddress || '',
        townCity: data[0]?.Consignor?.townCity || '',
        postCode: data[0]?.Consignor?.postCode || '',
        Country: data[0]?.Consignor?.Country?.toUpperCase?.() || '',
      };

      const consigneeData = {
        companyName: data[0]?.Consignee?.companyName || '',
        EORI: data[0]?.Consignee?.EORI || '',
        streetAddress: data[0]?.Consignee?.streetAddress || '',
        townCity: data[0]?.Consignee?.townCity || '',
        postCode: data[0]?.Consignee?.postCode || '',
        Country: data[0]?.Consignee?.Country?.toUpperCase?.() || '',
      };

      const placeOfLoading = {
        country: data[0]?.PlaceOfLoading?.country || '',
        placeName: data[0]?.PlaceOfLoading?.placeName || '',
        postcode: data[0]?.PlaceOfLoading?.postcode || '',
      };

      const placeOfUnloading = {
        country: data[0]?.PlaceOfUnloading?.country || '',
        placeName: data[0]?.PlaceOfUnloading?.placeName || '',
        postcode: data[0]?.PlaceOfUnloading?.postcode || '',
      };

      data[0].lines.forEach((line) => {
        const currentLine = {
          id: idCount,
          goodsDescription: line?.goodsDescription || '',
          grossWeight: line?.grossWeight?.toString?.() || '',
          numPackages: line?.numPackages?.toString?.() || '',
          numPieces: line?.numPieces?.toString?.() || '',
          packageType: line?.packageType?.toString?.() || '241', //default to 241 - package - if package type invalid or not provided
          packagingMarks: line?.packagingMarks || 'As Addressed',
          dangerousGoods: line?.dangerousGoods || '',
        };
        lines.push(currentLine);
        idCount--;
      });

      setCurrentConsignment((prev) => ({
        ...prev,
        Consignor: {
          ...consignorData,
        },
        Consignee: {
          ...consigneeData,
        },
        PlaceOfLoading: {
          ...placeOfLoading,
        },
        PlaceOfUnloading: {
          ...placeOfUnloading,
        },
        lines: lines,
      }));
    }
  };

  return {
    addNewConsignment,
    editConsignment,
    updateCurrentConsignment,
    saveCurrentConsignment,
    moveConsignmentData,
    setBlankConsignmentData,
    selectNeenor,
    deleteConsignmentData,
    addNewConsignmentLine,
    updateCurrentLine,
    editConsignmentLine,
    saveConsignmentLine,
    deleteConsignmentLine,
    deleteConsignment,
    hasConsignments,
    currentLine,
    handleGetMRNDetails,
  };
};

export default useConsignment;
